import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReviewQueueItem, ReviewQueueState, ParsedOrderData, FlagReason } from '../../types/reviewQueue';

const REVIEW_QUEUE_STORAGE_KEY = 'review-queue';

/**
 * Loads review queue items from localStorage
 */
function loadReviewQueueFromStorage(): ReviewQueueItem[] {
  console.log('[ReviewQueue] Loading review queue from localStorage');

  try {
    const stored = localStorage.getItem(REVIEW_QUEUE_STORAGE_KEY);
    if (!stored) {
      console.log('[ReviewQueue] No data found');
      return [];
    }

    const parsed = JSON.parse(stored);

    if (!Array.isArray(parsed)) {
      console.warn('[ReviewQueue] Data is not an array');
      return [];
    }

    console.log(`[ReviewQueue] ✓ Loaded ${parsed.length} items`);
    return parsed;
  } catch (error) {
    console.error('[ReviewQueue] Failed to load from storage:', error);
    return [];
  }
}

// Initial state
const initialState: ReviewQueueState = {
  items: loadReviewQueueFromStorage()
};

const reviewQueueSlice = createSlice({
  name: 'reviewQueue',
  initialState,
  reducers: {
    addReviewItem: (state, action: PayloadAction<ReviewQueueItem>) => {
      state.items.push(action.payload);
      console.log(
        `[ReviewQueue] ✓ Added item: ${action.payload.parsedData.customerName} (${action.payload.flagReason})`
      );
    },

    updateReviewItem: (
      state,
      action: PayloadAction<{
        id: string;
        parsedData: ParsedOrderData;
        flagReason?: FlagReason;
        flagDetails?: string;
      }>
    ) => {
      const item = state.items.find(i => i.id === action.payload.id);
      if (item) {
        item.parsedData = action.payload.parsedData;

        // Update flag reason and details if provided
        if (action.payload.flagReason !== undefined) {
          item.flagReason = action.payload.flagReason;
        }
        if (action.payload.flagDetails !== undefined) {
          item.flagDetails = action.payload.flagDetails;
        }

        console.log(`[ReviewQueue] ✓ Updated item: ${item.id} (flag: ${item.flagReason})`);
      }
    },

    removeReviewItem: (state, action: PayloadAction<string>) => {
      const itemIndex = state.items.findIndex(i => i.id === action.payload);
      if (itemIndex !== -1) {
        const item = state.items[itemIndex];
        state.items.splice(itemIndex, 1);
        console.log(`[ReviewQueue] ✓ Removed item: ${item.parsedData.customerName}`);
      }
    },

    clearReviewQueue: state => {
      state.items = [];
      console.log('[ReviewQueue] ✓ Cleared all items');
    }
  }
});

export const { addReviewItem, updateReviewItem, removeReviewItem, clearReviewQueue } = reviewQueueSlice.actions;

export default reviewQueueSlice.reducer;
